import * as Popover from '@radix-ui/react-popover'
import { format, isAfter, isBefore, parse } from 'date-fns'
import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'


import Datepicker from '@shared/ui/datepicker'
import { InputProps } from '@shared/ui/input'
import InputMask from '@shared/ui/input-mask'
import { isValidDateString } from '@shared/uttils'

import styles from './styles.module.scss'


interface InputDateProps extends Pick<InputProps, 'inputSize'> {
  value: Date | null | undefined;
  minDate?: Date;
  maxDate?: Date;
  theme?: 'grey';
  placeholder?: string;
  disabled?: boolean;
  onChange: (val: Date | null) => void;
}

const InputDate = forwardRef<HTMLInputElement, InputDateProps>((props, ref) => {
  const isMobile = useMediaQuery('(max-width: 575px)')
  const {
    value,
    minDate,
    maxDate,
    placeholder,
    inputSize,
    theme,
    disabled,
    onChange,
  } = props
  const [ localValue, setLocalValue ] = useState('')
  const [ open, setOpen ] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Передаем ref внутрь компонента
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

  useEffect(() => {
    if (value) {
      setLocalValue(format(value, 'dd.MM.yyyy'))
    }
    else {
      setLocalValue('')
    }
  }, [ value ])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setLocalValue(raw)

    if (isValidDateString(raw)) {
      const parsed = parse(raw, 'dd.MM.yyyy', new Date())

      if (!isNaN(parsed.getTime())) {
        const isTooEarly = minDate && isBefore(parsed, minDate)
        const isTooLate = maxDate && isAfter(parsed, maxDate)

        if (!isTooEarly && !isTooLate) {
          onChange(parsed)
        }
      }
    }
  }

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const isValid = isValidDateString(e.target.value)

    if (!isValid) {
      if (value) {
        setLocalValue(format(value, 'dd.MM.yyyy'))
      }
      else {
        setLocalValue('')
      }
    }
  }

  return (
    <Popover.Root open={open}>
      <div className={styles.root}>
        <InputMask
          ref={inputRef}
          mask="__.__.____"
          value={localValue}
          placeholder={placeholder}
          inputSize={inputSize}
          theme={theme}
          disabled={disabled}
          onChange={handleChange}
          onCustomClear={() => {
            onChange(null)
            setLocalValue('')
          }}
          onFocus={() => {
            if (!open) {
              setOpen(true)
            }
          }}
          onBlur={handleBlur}
          endDecorator={
            <ReactSVG
              className={styles.root__icon}
              src="/icons/calendar.svg"
              onClick={() => {
                inputRef.current?.focus()
              }}
            />
          }
        />

        <Popover.Trigger className={styles.root__trigger}></Popover.Trigger>

        <Popover.Content
          side="bottom"
          align={isMobile ? 'center' : 'start'}
          sideOffset={4}
          asChild
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            inputRef.current?.focus()
          }}
        >
          <Datepicker
            value={value as Date}
            onChange={(date) => {
              if (date instanceof Date) {
                onChange(date)
                setOpen(false)
              }
            }}
            onClose={() => {
              setOpen(false)
              inputRef.current?.blur()
            }}
          />
        </Popover.Content>
      </div>
    </Popover.Root>
  )
})

InputDate.displayName = 'InputDate'

export default InputDate
